import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import SearchResultGrid from "../searchResultGrid/searchResultGrid";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import apiClient from "../../axiosClient";
import entityPermissionsColDef from "../../colDef/entityPermissionsColDef";
import EntityPermissions from "../../interfaces/entityPermissions";
import HandleEntityPermissionsModal from "../modals/handleEntityPermissionsModal";
import Filter from "../../interfaces/filters";
import { buildQueryObjectFromRecord } from "../../utils/commonUtils";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import FilterDrawer from "../dashboard/filterDrawer";
import { getSelectedFiltersCount } from "../../utils/filterUtils";

const modalModes = { create: 'create', edit: 'edit' };

const ManageEntityPermissions: React.FC = () => {
    const clients = useSelector((state: RootState) => state.clients.clients).map(val => val.client_name);
    const agencies = useSelector((state: RootState) => state.agencies.agencies).map(val => val.agency_name);
    const permissions = useSelector((state: RootState) => state.permissions.permissions).filter(val => val.is_active).map(val => val.permission_name);
    const [entityPermissions, setEntityPermissions] = useState<EntityPermissions[]>([]);
    const [selectedEntityPermissions, setSelectedEntityPermissions] = useState<EntityPermissions[]>([]);
    const [addEntityPermissionModalOpen, setAddEntityPermissionModalOpen] = useState<boolean>(false);
    const [leftMargin, setLeftMargin] = useState<string>("16px");
    const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
    const [filters, setFilters] = useState<Filter[]>([
        { id: "entity_type" ,key: "entity_type", type: "dropdown", label: "Select Entity Type", list: ["Client", "Agency"], value: "", disabled: false, multiple: false, filterOptions: false },
        { id: "client_name" ,key: "entity_name", type: "dropdown", label: "Select Client", value: [], list: clients, disabled: false, multiple: true, filterOptions: false, visibility: { key: "entity_type", operation: "equals", value: "Client" } },
        { id: "agency_name" ,key: "entity_name", type: "dropdown", label: "Select Agency", value: [], list: agencies, disabled: false, multiple: true, filterOptions: false, visibility: { key: "entity_type", operation: "equals", value: "Agency" } },
        { id: "permission_name" ,key: "permission_name", type: "dropdown", label: "Select Permission Name", value: [], list: permissions, disabled: false, multiple: true, filterOptions: false },
        { id: "is_permission_granted" ,key: "is_permission_granted", type: "dropdown", label: "Is Permission Granted", list: ["true", "false"], value: "", disabled: false, multiple: false, filterOptions: false },
        { id: "license_count_le" ,key: "license_count_le", type: "number", label: "License Count Less Than", value: "", disabled: false, filterOptions: false, multiple: false },
        { id: "license_count_ge" ,key: "license_count_ge", type: "number", label: "License Count Greater Than", value: "", disabled: false, filterOptions: false, multiple: false },
    ]);
    const [modalMode, setModalMode] = useState<string>(modalModes.create);
    const [loading, setLoading] = useState<boolean>(false);

    const toggleDrawer = () => {
        setDrawerVisible(!drawerVisible);
        if (drawerVisible) {
            setLeftMargin("16px");
        } else {
            setLeftMargin("166px");
        }
    };

    const openModal = (mode: string) => {
        setModalMode(mode);
        setAddEntityPermissionModalOpen(true);
    };

    const fetchEntityPermissions = useCallback(async () => {
        try {
            setLoading(true);
            const filterQuery = buildQueryObjectFromRecord(filters);
            const response = await apiClient.get('/api/v1/entity_permissions?' + filterQuery);
            if (response.status === 200) {
                setEntityPermissions(response.data);
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchEntityPermissions();
    }, [filters]);

    const resetFilters = () => {
        setFilters(filters.map(val => {
            if (val.multiple) {
                val.value = [];
            } else {
                val.value = "";
            }
            return val;
        }));
    };

    const handleSelectedRows = (ids: GridRowSelectionModel) => {
        setSelectedEntityPermissions(entityPermissions.filter(val => ids.ids.has(val.entity_permission_id)));
    };

    return (
        <Box sx={{ ml: leftMargin, mr: 2, mt: 2, mb: 2, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                        variant={getSelectedFiltersCount(filters) === 0 ? "outlined" : "contained"}
                        onClick={toggleDrawer}
                    >
                        <Typography>Filters [{getSelectedFiltersCount(filters)}]</Typography>
                    </Button>
                    <Button variant="outlined" onClick={resetFilters} sx={{ ml: 2 }}>
                        <Typography>Reset</Typography>
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
                    {selectedEntityPermissions.length === 1 ? (
                        <Button sx={{ mr: 2 }} variant="outlined" onClick={() => openModal(modalModes.edit)}>
                            <Typography>Edit Entity Permission</Typography>
                        </Button>
                    ) : null}
                    <Button variant="outlined" onClick={() => openModal(modalModes.create)}>
                        <Typography>Add Entity Permission</Typography>
                    </Button>
                </Box>
            </Box>
            {loading ? (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress color="primary" />
                </Box>
            ) : (
                <SearchResultGrid
                    columns={entityPermissionsColDef}
                    results={entityPermissions}
                    rowCount={entityPermissions.length}
                    onSelectRow={handleSelectedRows}
                    getRowId={(row: EntityPermissions) => row.entity_permission_id}
                />
            )}
            {addEntityPermissionModalOpen && (
                <HandleEntityPermissionsModal
                    open={addEntityPermissionModalOpen}
                    onClose={() => setAddEntityPermissionModalOpen(false)}
                    selectedEntityPermission={selectedEntityPermissions.length > 0 ? selectedEntityPermissions[0] : undefined}
                    modalMode={modalMode}
                    getAPIFunction={fetchEntityPermissions}
                />
            )}
            <FilterDrawer
                drawerOpen={drawerVisible}
                toggleDrawer={toggleDrawer}
                filters={filters}
                setFilters={setFilters}
            />
        </Box>
    );
};

export default ManageEntityPermissions;